import { useState, useEffect } from "react";
import api from "../../services/api";
// NOTE: TwoFactorSettings.css is hoisted from App.jsx (this component is pulled
// into more than one lazy chunk — Enforce2FAGate in the main bundle and
// EmpSettingsPage in each role chunk — so a local import would race the CSS
// order and fail `CI=true npm run build`).

// Reusable "Two-Step Verification" card. Drop <TwoFactorSettings /> into any
// settings page. Flow: enable → scan QR → confirm 6-digit code → done.
//
// There is no Disable control and no backup codes, by policy. That makes the
// authenticator app the single second factor, so the enrol screen states two
// things up front before the user commits: keep the app, and only an
// administrator can undo this.
export default function TwoFactorSettings({ onEnabled } = {}) {
  const [enabled, setEnabled] = useState(null);   // null = still loading
  const [stage, setStage] = useState("idle");     // idle | setup | done
  const [qr, setQr] = useState("");
  const [manualKey, setManualKey] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const loadStatus = async () => {
    try { const s = await api.twoFactorStatus(); setEnabled(!!s.enabled); }
    catch { setEnabled(false); }
  };
  useEffect(() => { loadStatus(); }, []);

  const startSetup = async () => {
    setErr(""); setMsg(""); setBusy(true);
    try {
      const s = await api.twoFactorSetup();
      setQr(s.qr); setManualKey(s.manualKey); setCode(""); setStage("setup");
    } catch (e) { setErr(e.message || "Could not start setup."); }
    finally { setBusy(false); }
  };

  const confirmEnable = async (e) => {
    e?.preventDefault?.();
    if (!/^\d{6}$/.test(code.trim())) { setErr("Enter the 6-digit code from your app."); return; }
    setErr(""); setBusy(true);
    try {
      await api.twoFactorEnable(code.trim());
      setEnabled(true); setStage("done");
      setQr(""); setManualKey(""); setCode("");
    } catch (e) { setErr(e.message || "Invalid code."); }
    finally { setBusy(false); }
  };

  const copyKey = async () => {
    try { await navigator.clipboard.writeText(manualKey); setMsg("Setup key copied."); }
    catch { setErr("Could not copy — select the key and copy it manually."); }
  };

  return (
    <div className="tfa-card">
      <div className="tfa-head">
        <div className="tfa-head-row">
          <div className="tfa-head-main">
            <div className="tfa-title">🛡️ Two-Step Verification</div>
            <div className="tfa-subtitle">Add a second step when you sign in</div>
          </div>
          {enabled !== null && (
            <span className={`tfa-badge ${enabled ? "tfa-badge--on" : "tfa-badge--off"}`}>
              {enabled ? "On" : "Off"}
            </span>
          )}
        </div>
      </div>

      <div className="tfa-body">
        {msg && <div className="tfa-alert tfa-alert--success">✓ {msg}</div>}
        {err && <div className="tfa-alert tfa-alert--error">⚠ {err}</div>}

        <div className="tfa-intro">
          After your password, sign-in asks for a 6-digit code from an authenticator app
          on your phone. The code changes every 30 seconds.
        </div>

        {enabled === null && <div className="tfa-loading">Loading…</div>}

        {/* ON — status only. No disable control by design. */}
        {enabled === true && stage !== "done" && (
          <>
            <div className="tfa-note tfa-note--success">
              Your account is protected. You’ll be asked for a code from your authenticator
              app each time you sign in.
            </div>
            <div className="tfa-note tfa-note--info">
              <b>Keep that app installed.</b> Two-step verification can’t be turned off from
              here. If you change or lose your phone, ask your system administrator to reset
              it for you — then set it up again on the new device.
            </div>
          </>
        )}

        {/* OFF — the commitment, then one button */}
        {enabled === false && stage === "idle" && (
          <>
            <div className="tfa-note tfa-note--warn">
              <b>Before you start:</b> once this is on you can’t switch it off yourself — only a
              system administrator can. Set it up on a phone you’ll keep.
            </div>
            <button className="tfa-btn tfa-btn--mt" disabled={busy} onClick={startSetup}>
              {busy ? "Starting…" : "Set up Two-Step Verification"}
            </button>
          </>
        )}

        {/* SETUP — numbered because the order genuinely matters */}
        {stage === "setup" && (
          <div>
            <div className="tfa-step">
              <span className="tfa-step-num">1</span>
              <span className="tfa-step-title">Scan this QR code with your authenticator app</span>
            </div>

            <div className="tfa-qr-row">
              {qr && <img className="tfa-qr-img" src={qr} alt="Two-step verification QR code" />}
              <div className="tfa-qr-info">
                <div className="tfa-qr-apps">
                  Works with <b>Google Authenticator</b> and <b>Microsoft Authenticator</b>.
                </div>
                <div className="tfa-key-label">Can’t scan? Enter this key</div>
                <code className="tfa-key">{manualKey}</code>
                <button type="button" className="tfa-btn tfa-btn--ghost tfa-btn--sm" onClick={copyKey}>
                  Copy key
                </button>
              </div>
            </div>

            <form onSubmit={confirmEnable}>
              <div className="tfa-step">
                <span className="tfa-step-num">2</span>
                <span className="tfa-step-title">Enter the 6-digit code the app shows</span>
              </div>
              <div className="tfa-step-body">
                <input
                  className="tfa-input tfa-otp-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  aria-label="6-digit code from your authenticator app"
                  autoFocus
                />
                <div className="tfa-btn-row">
                  <button className="tfa-btn" disabled={busy || code.length !== 6} type="submit">
                    {busy ? "Verifying…" : "Verify & Turn On"}
                  </button>
                  <button
                    type="button"
                    className="tfa-btn tfa-btn--ghost"
                    onClick={() => { setStage("idle"); setErr(""); setCode(""); }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* DONE */}
        {stage === "done" && (
          <>
            <div className="tfa-note tfa-note--success">
              <div className="tfa-done-title">Two-step verification is on</div>
              <div className="tfa-done-desc">
                From your next sign-in, you’ll enter a code from your authenticator app after
                your password. Keep the app on your phone — only a system administrator can
                turn this off.
              </div>
            </div>
            <button
              className="tfa-btn tfa-btn--mt"
              onClick={() => { setStage("idle"); loadStatus(); onEnabled?.(); }}
            >
              Done
            </button>
          </>
        )}
      </div>
    </div>
  );
}
